import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { Button } from 'primereact/button';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { MultiSelect } from 'primereact/multiselect';
import { Tooltip } from 'primereact/tooltip';
import { FilterMatchMode } from 'primereact/api';
import { InputText } from 'primereact/inputtext';
import { IconField } from 'primereact/iconfield';
import { InputIcon } from 'primereact/inputicon';

const PAGE_SIZE = 10;

const BrowseTable = ({ browseData, criteria }) => {


  const dt = useRef(null);
  const [tableData, setTableData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [visibleColumns, setVisibleColumns] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [hasNextPage, setHasNextPage] = useState(true);
  const [filters, setFilters] = useState({
    global: { value: null, matchMode: FilterMatchMode.CONTAINS },
  });

  // Determine columns based on initial data
  const hasAnPosition = browseData.some(row => row?.anPosition !== undefined);
  const baseColumns = [
    { field: 'protein.status', header: 'Status' },
    { field: 'protein.gene.geneSymbol', header: 'Gene' },
    { field: 'protein.proteinAn', header: 'Accession' },
  ];
  const optionalColumns = hasAnPosition ? [{ field: 'anPosition', header: 'Residue' }] : [];
  const remainingColumns = [
    { field: 'protein.seqLength', header: 'Length' },
    { field: 'protein.summary', header: 'Protein Features Overview' },
  ];
  const browseColumns = [...baseColumns, ...optionalColumns, ...remainingColumns];

  // Set initial state based on browseData
  useEffect(() => {
    setTableData(browseData);
    setVisibleColumns(browseColumns);
    setHasNextPage(browseData.length === PAGE_SIZE);
    setCurrentPage(1);
  }, [browseData]); // Rerun only when browseData prop changes

  // Unified data fetching function
  const fetchData = async (page) => {
    setLoading(true);
    setCurrentPage(page);
    const offset = (page - 1) * PAGE_SIZE;
    let newData = [];

    try {
      let query;
      if (hasAnPosition) {
        query = {
          query: `
            query ResiduesByCriteria($criteria: [CriterionInput!]!, $offset: Int!, $limit: Int!) {
              getResiduesByCriteria(criteria: $criteria, pagination: { offset: $offset, limit: $limit }) {
                anPosition
                protein { gene { geneSymbol } status proteinAn seqLength summary }
              }
            }
          `,
          variables: { criteria, offset, limit: PAGE_SIZE },
        };
      } else {
        query = {
          query: `
            query ProteinsByCriteria($criteria: [CriterionInput!]!, $offset: Int!, $limit: Int!) {
              getProteinsByCriteria(criteria: $criteria, pagination: { offset: $offset, limit: $limit }) {
                protein { gene { geneSymbol } proteinAn status seqLength summary }
              }
            }
          `,
          variables: { criteria, offset, limit: PAGE_SIZE },
        };
      }
      const { data } = await axios.post('https://api.atlantis.bioinfolab.sns.it/graphql', query);
      newData = hasAnPosition
        ? data?.data?.getResiduesByCriteria || []
        : data?.data?.getProteinsByCriteria || [];

      setTableData(newData);
      setHasNextPage(newData.length === PAGE_SIZE);
    } catch (error) {
      console.error("Failed to fetch data:", error);
      setTableData([]);
      setHasNextPage(false);
    } finally {
      setLoading(false);
    }
  };

  const goToNextPage = () => {
    if (hasNextPage) {
      fetchData(currentPage + 1);
    }
  };

  const goToPrevPage = () => {
    if (currentPage > 1) {
      fetchData(currentPage - 1);
    }
  };

  // Handlers and templates...
  const onGlobalFilterChange = (e) => {
    const value = e.target.value;
    setFilters({ ...filters, global: { ...filters.global, value } });
  };

  const onColumnToggle = (e) => {
    const selectedFields = e.value;
    const updated = browseColumns.filter(col => selectedFields.includes(col.field));
    setVisibleColumns(updated);
  };

const formatSummaryForCSV = (summary) => {

if (!summary) return '';

return Object.entries(summary)

.map(([key, value]) => `${key}: ${value}`)

.join('; ');

};

const getFormattedDataForExport = () => {

return tableData.map(row => ({
...row, protein: { ...row.protein,
summary: formatSummaryForCSV(row.protein.summary)
}
}));
};

const exportCSV = () => { const formattedData = getFormattedDataForExport();
setTableData(formattedData);
setTimeout(() => {
dt.current.exportCSV();
setTableData(tableData);
}, 100);
 };

// Row click abre url externa
  const onRowClick = (e) => {
    const gene = e.data?.protein?.gene?.geneSymbol;
    const residue = e.data?.anPosition;

    console.log(residue)

    if (gene && !hasAnPosition) {
      window.open(
        `https://atlantis.bioinfolab.sns.it/explore/?target=${gene}&type=gene`,
        '_blank',
        'noopener,noreferrer'
      )
    } else if (residue && hasAnPosition){
          window.open(
        `https://atlantis.bioinfolab.sns.it/explore/?target=${residue}&type=residue`,
        '_blank',
        'noopener,noreferrer'
      )
      }
  };

 // Summary Body Template
  const importantSummaryKeys = ['pdb', 'ptm', 'binding' ,'active', 'clinvar', 'intraContact', 'interContact', 'bindingRegion'];
  const summaryDescriptions = {
    pdb: 'Protein Data Bank entries',
    ptm: 'PTMs PhosphoSitePlus®',
    clinvar: 'ClinVar Variant',
    binding: 'Binding sites',
    active: 'Active sites',
    intraContact: 'Intra-protein contacts',
    interContact: 'Inter-protein contacts',
    bindingRegion: 'IntAct Binding Regions'
  };

  const summaryBodyTemplate = (rowData) => {
    const summary = rowData.protein.summary || {};
    return (
      <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
        {importantSummaryKeys.map(key => {
          const value = summary[key];
          if (value === undefined || value === 0) return null;
          return (
            <span
              key={key}
              className="p-tag p-tag-info"
              style={{ cursor: 'default' }}
              data-pr-tooltip={`${summaryDescriptions[key] || key}: ${value}`}
            >
              {key}: {value}
            </span>
          );
        })}
      </div>
    );
  };

  const renderBrowseColumns = visibleColumns.map(({ field, header }) => {
    if (field === 'protein.summary') {
      return (
        <Column
          key={field}
          field={field}
          header={header}
          body={summaryBodyTemplate}
          sortable={false}
        />
      );
    }
    return <Column key={field} field={field} header={header} sortable />;
  });

  // Header and Footer elements
  const header = (
     <div className="row align-items-center">
      <div className="col mb-2">
        <IconField iconPosition="left">
          <InputIcon className="pi pi-search" />
          <InputText value={filters.global.value || ''} onChange={onGlobalFilterChange} placeholder="Global Search" />
        </IconField>
      </div>
      <div className="col" style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end' }}>
        <MultiSelect
          value={visibleColumns.map(col => col.field)}
          options={browseColumns.map(c => ({ label: c.header, value: c.field }))}
          onChange={onColumnToggle}
          optionLabel="label"
          placeholder="Select Columns"
          display="chip"
          style={{ width: '200px', marginRight: '10px' }}
        />
        <Button type="button" icon="pi pi-file" rounded onClick={exportCSV} tooltip="CSV" />
      </div>
    </div>
  );

  const paginatorFooter = (
    <div className="p-paginator-footer" style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', padding: '1rem' }}>
      <Button icon="pi pi-angle-left" className="p-button-text" onClick={goToPrevPage} disabled={currentPage === 1 || loading} />
      <span className="p-paginator-current" style={{ margin: '0 1rem' }}>{currentPage}</span>
      <Button icon="pi pi-angle-right" className="p-button-text" onClick={goToNextPage} disabled={!hasNextPage || loading} />
    </div>
  );

  return (
    <>
      <Tooltip target=".p-tag-info" />
      <DataTable
        ref={dt}
        value={tableData}
        header={header}
        footer={paginatorFooter}
        sortField="protein.gene.geneSymbol"
        sortOrder={1}
        rows={PAGE_SIZE}
        loading={loading}
        filters={filters}
        onFilter={(e) => setFilters(e.filters)}
        responsiveLayout="scroll"
        tableStyle={{ minWidth: '60rem' }}
        onRowClick={onRowClick}
        selectionMode="single"
      >
        {renderBrowseColumns}
      </DataTable>
    </>
  );
};

export default BrowseTable;